import React, { useState, useEffect } from 'react';
import { GamingService } from '../services/gaming';

const LeaderboardDisplay = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(100);

  useEffect(() => {
    loadLeaderboard();
  }, [limit]);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const data = await GamingService.getLeaderboard(limit);
      setLeaderboard(data);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'from-yellow-400 to-yellow-600';
    if (rank === 2) return 'from-gray-300 to-gray-500';
    if (rank === 3) return 'from-orange-400 to-orange-600';
    return 'from-gray-700 to-gray-800';
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '👑';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-2xl text-white">Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-red-500 bg-clip-text text-transparent">
          Global Leaderboard
        </h1>
        <p className="text-gray-400">Top {limit} players in Milkman's Twisted Universe</p>
      </div>

      {/* Top 3 Podium */}
      {leaderboard.length >= 3 && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          {/* 2nd Place */}
          <div className="col-span-1 mt-8">
            <div className="bg-gradient-to-br from-gray-300 to-gray-500 p-1 rounded-lg">
              <div className="bg-gray-900 p-6 rounded-lg text-center">
                <div className="text-6xl mb-3">🥈</div>
                <div className="text-2xl font-bold text-white mb-2">
                  {leaderboard[1].username}
                </div>
                <div className="text-gray-400 text-sm mb-2">{leaderboard[1].rank}</div>
                <div className="text-xl text-gray-300">
                  <div>{leaderboard[1].wins} Wins</div>
                  <div className="text-sm text-gray-500">
                    Level {leaderboard[1].level} • {leaderboard[1].kills} Kills
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 1st Place */}
          <div className="col-span-1">
            <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 p-1 rounded-lg transform scale-110">
              <div className="bg-gray-900 p-8 rounded-lg text-center">
                <div className="text-7xl mb-4 animate-bounce">👑</div>
                <div className="text-3xl font-bold text-yellow-400 mb-2">
                  {leaderboard[0].username}
                </div>
                <div className="text-gray-300 text-sm mb-3">{leaderboard[0].rank}</div>
                <div className="text-2xl text-white">
                  <div>{leaderboard[0].wins} Wins</div>
                  <div className="text-sm text-gray-400">
                    Level {leaderboard[0].level} • {leaderboard[0].kills} Kills
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 3rd Place */}
          <div className="col-span-1 mt-8">
            <div className="bg-gradient-to-br from-orange-400 to-orange-600 p-1 rounded-lg">
              <div className="bg-gray-900 p-6 rounded-lg text-center">
                <div className="text-6xl mb-3">🥉</div>
                <div className="text-2xl font-bold text-white mb-2">
                  {leaderboard[2].username}
                </div>
                <div className="text-gray-400 text-sm mb-2">{leaderboard[2].rank}</div>
                <div className="text-xl text-gray-300">
                  <div>{leaderboard[2].wins} Wins</div>
                  <div className="text-sm text-gray-500">
                    Level {leaderboard[2].level} • {leaderboard[2].kills} Kills
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rest of Leaderboard */}
      <div className="bg-gray-900/50 rounded-lg overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800">
              <tr>
                <th className="px-6 py-4 text-left text-white font-bold">Rank</th>
                <th className="px-6 py-4 text-left text-white font-bold">Player</th>
                <th className="px-6 py-4 text-left text-white font-bold">Title</th>
                <th className="px-6 py-4 text-center text-white font-bold">Level</th>
                <th className="px-6 py-4 text-center text-white font-bold">Wins</th>
                <th className="px-6 py-4 text-center text-white font-bold">Kills</th>
                <th className="px-6 py-4 text-center text-white font-bold">Matches</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.slice(3).map((player, index) => {
                const rank = index + 4;
                return (
                  <tr
                    key={index}
                    className="border-t border-gray-800 hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className={`inline-block px-3 py-1 rounded-lg bg-gradient-to-r ${getRankColor(rank)} text-white font-bold`}>
                        #{rank}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-white font-bold">
                      {player.username}
                    </td>
                    <td className="px-6 py-4 text-purple-400">
                      {player.rank}
                    </td>
                    <td className="px-6 py-4 text-center text-yellow-400 font-bold">
                      {player.level}
                    </td>
                    <td className="px-6 py-4 text-center text-green-400 font-bold">
                      {player.wins}
                    </td>
                    <td className="px-6 py-4 text-center text-red-400 font-bold">
                      {player.kills}
                    </td>
                    <td className="px-6 py-4 text-center text-gray-400">
                      {player.matches_played}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardDisplay;